import { useOktaAuth } from '@okta/okta-react';
import { Env, useEnv } from '../../Context/EnvironmentProvider';

type CancelJobRoleProp = {
  [key in Env]: {
    roles: string[];
  };
};
type SetStableRoleProp = {
  [key in Env]: {
    roles: string[];
  };
};
type PromotionRolePropItem = {
  promotable: boolean;
  promoteTo?: string;
  promoteRoles?: string[];
};

type PromotionRoleProp = {
  [key in Env]: PromotionRolePropItem;
};

type RoleAuthProps = {
  cancelJobRoleAuth: CancelJobRoleProp;
  promotionRoleAuth: PromotionRoleProp;
  setStableRoleAuth: SetStableRoleProp;
};

const roleAuthMatrix: RoleAuthProps = {
  cancelJobRoleAuth: {
    branch: {
      roles: ['Canopy Condor Developer'],
    },
    dev: {
      roles: ['Canopy Condor Developer'],
    },
    staging: {
      roles: [
        'Canopy Condor Developer',
        'Canopy Condor QE',
        'Canopy Condor Release Manager',
      ],
    },
    prod: {
      roles: ['Canopy Condor Release Manager'],
    },
  },
  setStableRoleAuth: {
    branch: {
      roles: ['Canopy Condor Developer'],
    },
    dev: {
      roles: ['Canopy Condor Developer'],
    },
    staging: {
      roles: [
        'Canopy Condor Developer',
        'Canopy Condor QE',
        'Canopy Condor Release Manager',
      ],
    },
    prod: {
      roles: ['Canopy Condor Release Manager'],
    },
  },
  promotionRoleAuth: {
    branch: {
      promotable: true,
      promoteTo: 'dev',
      promoteRoles: ['Canopy Condor Developer'],
    },
    dev: {
      promotable: true,
      promoteTo: 'staging',
      promoteRoles: ['Canopy Condor Developer'],
    },
    staging: {
      promotable: true,
      promoteTo: 'prod',
      promoteRoles: ['Canopy Condor Developer', 'Canopy Condor QE'],
    },
    prod: {
      promotable: false,
    },
  },
};

export const useRoleAuthorization = () => {
  const { authState } = useOktaAuth();
  const { env } = useEnv();

  const userRoles = (authState?.idToken?.claims.groups as string[]) || [];

  const hasRole = (matrix: Record<string, { roles?: string[] }>): boolean => {
    const rolesForEnv = matrix[env]?.roles || [];
    return rolesForEnv.some((role) => userRoles.includes(role));
  };

  const canCancelJob = (): boolean => hasRole(roleAuthMatrix.cancelJobRoleAuth);

  const canSetStable = (): boolean => hasRole(roleAuthMatrix.setStableRoleAuth);

  const canPromotePackages = (): boolean => {
    const promotionInfo = roleAuthMatrix.promotionRoleAuth[env] ?? {
      promotable: false,
    };
    const hasPromotionRole =
      promotionInfo?.promoteRoles?.some((role) => userRoles.includes(role)) ||
      false;
    return promotionInfo?.promotable && hasPromotionRole;
  };

  const getPromotionTarget = (): string | undefined =>
    roleAuthMatrix.promotionRoleAuth[env]?.promoteTo?.toUpperCase();

  return {
    canCancelJob,
    getPromotionTarget,
    canPromotePackages,
    canSetStable,
  };
};
